// i18n/en.js
// PrizeReal — Texts in English (US / India)
// Single source of truth for all visible strings.
// Never hardcode text in components or screens.

const en = {

  // ─── General ───────────────────────────────────────────────────────────────
  app: {
    name:    "PrizeReal",
    tagline: "Share local. Show up. Get rewarded.",
    loading: "Loading...",
    error:   "Something went wrong. Please try again.",
    retry:   "Retry",
    save:    "Save",
    cancel:  "Cancel",
    confirm: "Confirm",
    close:   "Close",
    back:    "Back",
    next:    "Next",
    done:    "Done",
    yes:     "Yes",
    no:      "No",
  },

  // ─── Auth ──────────────────────────────────────────────────────────────────
  auth: {
    welcomeTitle:    "Welcome to PrizeReal",
    welcomeSubtitle: "Sign in to start earning rewards at local businesses.",
    signInGoogle:    "Sign in with Google",
    signInLoading:   "Signing in...",
    signOutButton:   "Sign out",
    signOutConfirm:  "Are you sure you want to sign out?",

    // SMS verification
    smsTitle:        "Verify your number",
    smsSubtitle:     "We sent a code to {phone}. Enter it below to continue.",
    smsPlaceholder:  "Your phone number",
    smsCodeLabel:    "Verification code",
    smsCodePlaceholder: "6 digits",
    smsSend:         "Send code",
    smsVerify:       "Verify",
    smsResend:       "Resend code",
    smsResendIn:     "Resend in {seconds}s",
    smsSuccess:      "Number verified ✓",
    smsError:        "Incorrect code. Please try again.",
    smsRequired:     "You need to verify your phone number to claim rewards.",

    errors: {
      googleFailed:  "Could not sign in with Google. Please try again.",
      phoneFailed:   "Could not send the code. Please check your number.",
      codeFailed:    "Incorrect or expired code.",
      networkError:  "No connection. Please check your internet.",
    },
  },

  // ─── Home ──────────────────────────────────────────────────────────────────
  home: {
    title:           "Businesses near you",
    subtitle:        "Share, show up in person, and win.",
    searchPlaceholder: "Search business...",
    filterAll:       "All",
    noResults:       "No businesses found in your area.",
    noResultsHint:   "Try a different search or check back later.",
    distanceUnit:    "m",
    distanceUnitKm:  "km",
    featured:        "Featured",
    closedToday:     "Closed today",
    clicksNeeded:    "{n} clicks to redeem",
    clicksOne:       "1 click to redeem",
  },

  // ─── Business card ─────────────────────────────────────────────────────────
  bizCard: {
    shareButton:     "Share",
    claimButton:     "Claim",
    prize:           "Prize: {prize}",
    friendPrize:     "Your friends get: {prize}",
    clicksProgress:  "{current}/{needed} clicks",
    planBadge:       "{plan}",
  },

  // ─── Share ─────────────────────────────────────────────────────────────────
  share: {
    title:           "Share {business}",
    subtitle:        "Send this link to your contacts. Every click counts.",
    linkCopied:      "Link copied!",
    copyLink:        "Copy link",
    shareWhatsApp:   "Share on WhatsApp",
    shareEmail:      "Share by Email",
    shareOther:      "More options",
    clickCount:      "{n} clicks received",
    clickCountOne:   "1 click received",
    clickCountZero:  "No clicks yet",
    prize:           "Your reward when you show up: {prize}",
    friendPrize:     "Your friends receive: {prize}",
    howItWorks:      "How it works",
    step1:           "You share the link",
    step2:           "Your friends open it",
    step3:           "You walk in and scan the QR",
    step4:           "You get your reward!",

    // Pre-filled messages for WhatsApp/Email
    whatsappMessage: "Check out this local business! Open the link and I get a reward — and you get a discount on your first visit too 👉 {url}",
    emailSubject:    "I'm recommending this place — we both win",
    emailBody:       "Hey! I wanted to share this local business I like. If you open the link, I can claim a reward — and you also get a discount on your first visit.\n\n{url}\n\nSee you around!",
  },

  // ─── Claim ─────────────────────────────────────────────────────────────────
  claim: {
    title:           "Claim your reward",
    subtitle:        "Scan the business QR code to confirm you're here.",
    scanButton:      "Scan QR",
    scanInstructions:"Point your camera at the business QR code.",
    scanSuccess:     "Valid QR!",
    verifying:       "Verifying...",
    locationRequired:"We need your location to confirm you're at the business.",
    locationGranted: "Location confirmed ✓",
    locationDenied:  "Please enable GPS to claim your reward.",
    tooFarAway:      "You are {distance}m from the business. You need to be within 300m.",
    qrExpired:       "The QR has expired. Ask the business to display a new one.",
    qrInvalid:       "QR not recognized. Make sure you're scanning a PrizeReal QR code.",
    mockLocation:    "We detected a simulated location. Please disable any fake GPS apps.",
    notEnoughClicks: "You need {needed} clicks to claim. You have {current}.",
    alreadyClaimed:  "You already claimed this reward. You can try again in {days} days.",
    rateLimited:     "You've reached today's claim limit. Try again tomorrow.",

    errors: {
      cameraPermission: "We need camera access to scan the QR code.",
      networkError:     "No connection. The claim could not be recorded.",
      serverError:      "Server error. Please try again.",
    },
  },

  // ─── Success ───────────────────────────────────────────────────────────────
  success: {
    title:           "Reward claimed!",
    subtitle:        "Show this screen to the business.",
    prize:           "{prize}",
    businessName:    "at {business}",
    timestamp:       "Claimed on {date} at {time}",
    ratingTitle:     "How was your experience?",
    ratingSubtitle:  "Your feedback helps other users.",
    ratingLabel:     "Rate {business}",
    ratingSubmit:    "Submit rating",
    ratingThanks:    "Thanks for rating!",
    reportProblem:   "Report a problem",
    backHome:        "Back to home",
    shareAgain:      "Share again",
  },

  // ─── Levels ────────────────────────────────────────────────────────────────
  levels: {
    title:           "Your level",
    totalClicks:     "{n} total clicks",
    currentLevel:    "Current level",
    nextLevel:       "Next level",
    clicksToNext:    "{n} more clicks to reach {level}",
    atMaxLevel:      "You've reached the top level!",
    leaderboardTitle:"City leaderboard",
    leaderboardEmpty:"No data yet. Be the first!",
    rank:            "#{n}",
    you:             "(you)",
    perks:           "Perks",
    perkDescriptions: {
      standard_prizes:  "Standard prizes at all businesses",
      plus_10_percent:  "+10% on all prizes",
      vip_offers:       "Exclusive VIP offers",
      early_access:     "Early access to new offers",
      top_leaderboard:  "Top of the city leaderboard",
      exclusive_prizes: "City Legend exclusive prizes",
    },
  },

  // ─── Plans (for businesses) ────────────────────────────────────────────────
  plans: {
    title:           "Plans for your business",
    subtitle:        "No setup fees. No long-term contracts.",
    perMonth:        "/mo",
    free:            "Free",
    mostPopular:     "Most popular",
    currentPlan:     "Current plan",
    upgrade:         "Upgrade",
    downgrade:       "Change plan",
    cancel:          "Cancel subscription",
    cancelConfirm:   "Are you sure you want to cancel? You'll lose your plan at the end of the billing period.",
    earlyPartner:    "3 months free for early partners",
  },

  // ─── Dispute ───────────────────────────────────────────────────────────────
  dispute: {
    title:           "Report a problem",
    subtitle:        "Describe what happened. Our team reviews it within 48 hours.",
    typeLabel:       "Problem type",
    types: {
      prize_not_given:   "The business didn't give me the prize",
      qr_not_working:    "The QR code wasn't working",
      wrong_prize:       "The prize was different from what was advertised",
      location_error:    "Location error",
      other:             "Other",
    },
    descriptionLabel:    "Description",
    descriptionPlaceholder: "Tell us what happened with as much detail as possible.",
    submit:              "Submit report",
    submitSuccess:       "Report submitted. We'll get back to you within 48 hours.",
    submitError:         "Could not submit report. Please try again.",
    slaNote:             "Response time: within 48 hours.",
  },

  // ─── Navbar ────────────────────────────────────────────────────────────────
  nav: {
    home:    "Home",
    levels:  "Levels",
    profile: "Profile",
  },

  // ─── Generic errors ────────────────────────────────────────────────────────
  errors: {
    notFound:       "Page not found.",
    unauthorized:   "You need to sign in.",
    offline:        "No internet connection.",
    generic:        "An unexpected error occurred.",
    tryAgain:       "Try again",
  },
};

export default en;
